import { useEffect, useRef, useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  Fade,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { useGlobalSearchStore } from "../stores/GlobalSearchStore";
import { useGlobalSearch } from "../services/GlobalSearchService";
import { useDebounce } from "src/reusable/hooks/useDebounce";
import { useSearchResultsNavigation } from "../hooks/useGlobalSearchKeyboard";
import GlobalSearchInput from "./GlobalSearchInput";
import GlobalSearchResults from "./GlobalSearchResults";
import GlobalSearchEmptyState from "./GlobalSearchEmptyState";
import MovedToLibraryNotice from "src/components/MovedToLibraryNotice";
import {
  SEARCH_DEBOUNCE_MS,
  KEYBOARD_SHORTCUTS,
} from "../constants/globalSearch.constants";

export default function GlobalSearchModal() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [immediateQuery, setImmediateQuery] = useState<string | null>(null);

  const {
    isOpen,
    query,
    keywordOnly,
    closeSearch,
    setQuery,
    addRecentSearch,
    recentSearches,
  } = useGlobalSearchStore();

  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);

  // Use immediate query if set (Enter pressed), otherwise use debounced
  const searchQuery = immediateQuery ?? debouncedQuery;

  const {
    data: results,
    isLoading,
    isError,
  } = useGlobalSearch(searchQuery, keywordOnly, isOpen);

  // Clear immediate query when debounced catches up or query changes
  useEffect(() => {
    if (immediateQuery && debouncedQuery === immediateQuery) {
      setImmediateQuery(null);
    }
  }, [debouncedQuery, immediateQuery]);

  // Reset immediate query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImmediateQuery(null);
    }
  }, [isOpen]);

  // Handle Enter key to trigger immediate search
  const handleSearchSubmit = useCallback(() => {
    if (query.trim().length >= 2) {
      setImmediateQuery(query);
    }
  }, [query]);

  const handleSelectResult = useCallback(
    (index: number) => {
      if (results && results[index]) {
        const result = results[index];
        addRecentSearch(query);
        window.open(result.navigateTo, "_blank");
      }
    },
    [results, query, addRecentSearch],
  );

  const { selectedIndex, handleKeyDown } = useSearchResultsNavigation(
    results?.length || 0,
    handleSelectResult,
  );

  // Focus input when modal opens (backup for autoFocus)
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Delay must exceed dialog animation (200ms) to ensure focus works
      const timer = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Add keydown listener for navigation
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleResultClick = (index: number) => {
    handleSelectResult(index);
  };

  const showEmptyState =
    !isLoading && (!results || results.length === 0) && searchQuery.length >= 2;
  const showResults = !isLoading && results && results.length > 0;
  const showRecentSearches = !query && recentSearches.length > 0;
  const showInitialState = !query && recentSearches.length === 0;

  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  return (
    <Dialog
      open={isOpen}
      onClose={closeSearch}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          position: "fixed",
          top: "15%",
          borderRadius: 2,
          maxHeight: "70vh",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
      data-testid="global-search-modal"
      data-attr="global-search:modal:search"
      aria-labelledby="global-search-title"
    >
      <Paper elevation={0}>
        <GlobalSearchInput
          ref={inputRef}
          value={query}
          onChange={setQuery}
          onSubmit={handleSearchSubmit}
          isLoading={isLoading}
        />

        {/* This modal already hands off to the pane on workspace pages
            (#2377); the notice covers everywhere else it still serves. */}
        <MovedToLibraryNotice
          noticeId="global-search"
          dense
          onNavigate={closeSearch}
          sx={{ borderRadius: 0 }}
        >
          In the AtomAssist workspace, search now lives in the Library pane.
        </MovedToLibraryNotice>

        <DialogContent
          sx={{
            p: 0,
            maxHeight: "calc(70vh - 120px)",
            overflow: "auto",
          }}
          dividers
        >
          {showInitialState && <GlobalSearchEmptyState type="initial" />}

          {showRecentSearches && (
            <GlobalSearchEmptyState
              type="recent"
              recentSearches={recentSearches}
              onSelectRecent={(search) => setQuery(search)}
            />
          )}

          {showEmptyState && (
            <GlobalSearchEmptyState type="no-results" query={searchQuery} />
          )}

          {isError && <GlobalSearchEmptyState type="error" />}

          {showResults && (
            <GlobalSearchResults
              results={results}
              selectedIndex={selectedIndex}
              onResultClick={handleResultClick}
              searchQuery={searchQuery}
            />
          )}
        </DialogContent>

        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {KEYBOARD_SHORTCUTS.navigate} Navigate
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {KEYBOARD_SHORTCUTS.select} Select
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {KEYBOARD_SHORTCUTS.close} Close
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {isMac
              ? KEYBOARD_SHORTCUTS.toggle.mac
              : KEYBOARD_SHORTCUTS.toggle.windows}{" "}
            Open
          </Typography>
        </Box>
      </Paper>
    </Dialog>
  );
}
